import { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Divider,
  IconButton,
  Tabs,
  Tab,
  Button,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlanesForm from './parametros/PlanesForm';
import TiersForm from './parametros/TiersForm';
import BonosForm from './parametros/BonosForm';
import ValesForm from './parametros/ValesForm';
import ChargebackForm from './parametros/ChargebackForm';
import DatosForm from './parametros/DatosForm';
import JefaturaForm from './parametros/JefaturaForm';
import LiderForm from './parametros/LiderForm';
import CoordinadorPorcentajesForm from './parametros/CoordinadorPorcentajesForm';
import PeriodosForm from './parametros/PeriodosForm';
import AuditoriaTimeline from './parametros/AuditoriaTimeline';
import { usePeriodo } from '../context/PeriodoContext';

interface DrawerParametrizacionProps {
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`param-tabpanel-${index}`}
      aria-labelledby={`param-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function DrawerParametrizacion({ open, onClose }: DrawerParametrizacionProps) {
  const [tabValue, setTabValue] = useState(0);
  const [showBanner, setShowBanner] = useState(false);
  const { setPeriodo, setPeriodoCallCenter } = usePeriodo();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleParametroGuardado = () => {
    setShowBanner(true);
    // El banner se muestra cuando se guarda cualquier parámetro
  };

  const handlePeriodoHomeChange = (fechaInicio: string, fechaFin: string) => {
    setPeriodo(fechaInicio, fechaFin);
  };

  const handlePeriodoCallCenterChange = (fechaInicio: string, fechaFin: string) => {
    setPeriodoCallCenter(fechaInicio, fechaFin);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 600, md: 800 } }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Parametrización de Comisiones</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        {/* Banner de recalcular */}
        {showBanner && (
          <Alert
            severity="warning"
            onClose={() => setShowBanner(false)}
            sx={{ m: 2, mb: 0 }}
          >
            Parámetros actualizados. Recalcula en el tab "Asesor" para aplicar los cambios.
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Períodos" />
            <Tab label="Planes" />
            <Tab label="Tiers" />
            <Tab label="Bonos" />
            <Tab label="Vales" />
            <Tab label="Chargeback" />
            <Tab label="Jefatura" />
            <Tab label="Líder" />
            <Tab label="Supervisor" />
            <Tab label="Datos" />
            <Tab label="Auditoría" />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          <TabPanel value={tabValue} index={0}>
            <PeriodosForm
              onPeriodoHomeChange={handlePeriodoHomeChange}
              onPeriodoCallCenterChange={handlePeriodoCallCenterChange}
              onSaved={handleParametroGuardado}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <PlanesForm onSaved={handleParametroGuardado} />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <TiersForm onSaved={handleParametroGuardado} />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <BonosForm onSaved={handleParametroGuardado} />
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <ValesForm onSaved={handleParametroGuardado} />
          </TabPanel>
          <TabPanel value={tabValue} index={5}>
            <ChargebackForm onSaved={handleParametroGuardado} />
          </TabPanel>
          <TabPanel value={tabValue} index={6}>
            <JefaturaForm onSaved={handleParametroGuardado} />
          </TabPanel>
          <TabPanel value={tabValue} index={7}>
            <LiderForm onSaved={handleParametroGuardado} />
          </TabPanel>
          <TabPanel value={tabValue} index={8}>
            <CoordinadorPorcentajesForm onSaved={handleParametroGuardado} />
          </TabPanel>
          <TabPanel value={tabValue} index={9}>
            <DatosForm onSaved={handleParametroGuardado} />
          </TabPanel>
          <TabPanel value={tabValue} index={10}>
            <AuditoriaTimeline />
          </TabPanel>
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>
            Cerrar
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
